import React, { useEffect } from 'react';

import { Box, CircularProgress, DialogActions, DialogTitle, Divider, Grid, TextField } from '@mui/material';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';

import AddressEditorContainer from '../AddressEditor';

import { profileLoadingState, selectedProfileState, useCorrectAddressCallback, useEditInstructionsCallback } from '../../states/profile';
import { useResolveCallback, incidentDetailState, useGotoListPageCallback } from '../../states/incidents';
import { useRecoilState, useRecoilValue } from 'recoil';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import Slide from '@mui/material/Slide';
import AddressInfoBox from '../../components/AddressInfoBox';
import { BlueBox } from '../../components/Box';
import Dev from '../Dev';
import { H4 } from '../../components/Heading';
import { loadingAddressState, useEnrichAddressCallback } from '../../states/address';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { selectedShipmentIdState, selectedShipmentState } from '../../states/shipment';
import { tokens } from '@gojitsu/web-ui-core';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

function CustomerProfileDetail({ originalAddress }) {
  const profile = useRecoilValue(selectedProfileState);
  const shipment = useRecoilValue(selectedShipmentState);
  const { corrected_address, id, address, instructions, total_customer_negative_feedbacks, total_customer_positive_feedbacks } = profile || {};
  const { dropoff_address } = shipment || {};
  const correctProfileAddress = useCorrectAddressCallback();
  const editInstructions = useEditInstructionsCallback();
  const enrichAddress = useEnrichAddressCallback();
  const loadingAddress = useRecoilValue(loadingAddressState);
  const loadingProfile = useRecoilValue(profileLoadingState);
  const [open, setOpen] = React.useState(false);
  const [openInstructionsDialog, setOpenInstructionsDialog] = React.useState(false);
  const [editingInstructions, setEditingInstructions] = React.useState();
  const shipmentId = useRecoilValue(selectedShipmentIdState);
  const [openVerifyLatLngOnOverrideDialog, setOpenVerifyLatLngOnOverrideDialog] = React.useState(false);

  const resolve = useResolveCallback();
  const selectedIncident = useRecoilValue(incidentDetailState);
  const gotoList = useGotoListPageCallback();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleEnrich = async () => {
    enrichAddress();
  };

  const correctAddress = (addr) => {
    correctProfileAddress(addr, shipmentId)
      .then(() => {
        if (selectedIncident && selectedIncident.type !== 'WRONG-DROPOFF-LOCATION') {
          resolve('RESOLVED').then(() => {
            // no-dd-sa:javascript-code-style/assignment-name
            const shipment_id = selectedIncident ? parseInt(selectedIncident.object.split('_')[1], 10) : null;
            toast.success(`Successfully Resolved Incident for shipment ${shipment_id}`);
            gotoList();
          });
        } else {
          toast.success(`Successfully updated shipment ${shipmentId} and customer profile`);
        }
      })
      .finally(() => {
        setOpen(false);
      });
  };

  const onAddressOverride = (addr) => {
    correctProfileAddress(addr, shipmentId, true)
      .then(() => {
        toast.success(`Successfully Updated Address Only for shipment ${shipmentId}`);
      })
      .finally(() => {
        setOpen(false);
        setOpenVerifyLatLngOnOverrideDialog(true);
      });
  };

  const saveInstructions = () => {
    editInstructions(editingInstructions, shipmentId);
    setOpenInstructionsDialog(false);
  };

  useEffect(() => {
    setEditingInstructions(instructions || '');
  }, [instructions]);

  return (
    <Grid container>
      <div style={{ position: 'relative', flex: 1 }}>
        <BlueBox>
          <Dev>
            <Typography style={{ fontSize: '0.8em' }}>{id}</Typography>
          </Dev>
          <AddressInfoBox origin={originalAddress || address || {}} address={corrected_address || address || originalAddress || {}} />
          <Divider style={{ marginTop: '10px' }} />

          <H4>Customer feedbacks</H4>
          <div>
            <span style={{ display: 'inline-flex', alignItems: 'center' }} className="legend-label">
              <ThumbUpIcon color='primary' />
              <Box component="span" sx={{ margin: '5px', color: 'blue', fontSize: '1.2em' }}>{total_customer_positive_feedbacks}</Box>
            </span>

            <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '30px' }} className="legend-label">
              <ThumbDownIcon color='error' />
              <Box component="span" sx={{ margin: '5px', color: tokens.red.red45, fontSize: '1.2em' }}>{total_customer_negative_feedbacks}</Box>
            </span>
          </div>

          <Divider style={{ marginTop: '10px' }} />
          <div style={{ position: 'relative' }}>
            <H4>Special Instructions</H4>
            {instructions && <div style={{ fontSize: '0.9em', color: '#555' }}>{instructions}</div>}
            <div style={{ position: 'absolute', top: 3, right: 3 }}>
              <Button size="small" onClick={() => setOpenInstructionsDialog(true)}>
                EDIT
              </Button>
            </div>
          </div>
        </BlueBox>
        {String(import.meta.env.REACT_APP_USE_BEANS).toLowerCase() === 'true' && (
          <div style={{ position: 'absolute', top: 3, right: 65 }}>
            <Button size="small" onClick={handleEnrich} disabled={loadingAddress}>
              ENRICH
            </Button>
          </div>
        )}
        <div style={{ position: 'absolute', top: 3, right: 3 }}>
          <Button size="small" onClick={handleClickOpen}>
            EDIT
          </Button>
        </div>
        {String(import.meta.env.REACT_APP_USE_BEANS).toLowerCase() === 'true' && (
          <div style={{ position: 'absolute', top: 3, right: 138, pointerEvents: loadingAddress ? 'none' : 'auto', opacity: loadingAddress ? 0.6 : 1 }}>
            <Link to={`/beans/${shipmentId}`} target="_blank">
              <Button size="small">BEANS</Button>
            </Link>
          </div>
        )}
      </div>
      <Dialog maxWidth="md" open={open} TransitionComponent={Transition} keepMounted onClose={handleClose} aria-labelledby="alert-dialog-slide-title" aria-describedby="alert-dialog-slide-description">
        <DialogContent>
          <div style={{ width: '800px' }}>
            <AddressEditorContainer orig={corrected_address || address || dropoff_address || {}} onSave={correctAddress} onOverride={onAddressOverride} onClose={handleClose} />
          </div>
        </DialogContent>
      </Dialog>
      <Dialog maxWidth="md" open={loadingAddress || loadingProfile} keepMounted aria-labelledby="alert-dialog-slide-title" aria-describedby="alert-dialog-slide-description">
        <DialogContent>
          <CircularProgress size={24} />
          {loadingAddress && <div>Enriching address...</div>}
          {loadingProfile && <div>Saving profile...</div>}
        </DialogContent>
      </Dialog>
      <Dialog
        maxWidth="md"
        open={openInstructionsDialog}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setOpenInstructionsDialog(false)}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle>Edit Special Instructions</DialogTitle>
        <DialogContent>
          <div style={{ width: '480px' }}>
            <TextField
              value={editingInstructions}
              fullWidth
              multiline
              maxRows={10}
              minRows={4}
              variant="outlined"
              onChange={(e) => setEditingInstructions(e.target.value)}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={saveInstructions}>Save</Button>
          <Button onClick={() => setOpenInstructionsDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
      <Dialog maxWidth="md" open={openVerifyLatLngOnOverrideDialog} keepMounted aria-labelledby="alert-dialog-slide-title" aria-describedby="alert-dialog-slide-description">
        <DialogTitle>Verify Latitude and Longitude</DialogTitle>
        <DialogContent>
          <div style={{ width: '480px' }}>You have overridden the address. Please verify the GPS coordinates (latitude and longitude) are correct.</div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenVerifyLatLngOnOverrideDialog(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default CustomerProfileDetail;
